import { Slot as SlotPrimitive } from 'radix-ui';
import React, { useImperativeHandle, useRef, useState } from 'react';

import { Button, Icon, SimpleTooltip, tv } from '@eluve/components';

const Slot = SlotPrimitive.Slot;

const contentEditableVariant = tv({
  base: '-mb-0.5 block border-b-2 border-transparent focus:border-bg-accent-strong-primary focus:outline-hidden',
  variants: {
    // Without the pencil button the text itself is the only affordance, so
    // it takes a text cursor and a hover underline instead.
    editButton: {
      true: '',
      false: 'cursor-text hover:border-border-secondary',
    },
  },
  defaultVariants: {
    editButton: true,
  },
});

interface ContentEditableProps extends React.HTMLAttributes<HTMLElement> {
  editButtonLabel?: string;
  onContentChange?: (content: string) => void;
  /**
   * When false, hides the pencil button — the content is edited by clicking
   * directly into the text (hover shows an underline affordance).
   * @default true
   */
  editButton?: boolean;
}

export const ContentEditable = React.forwardRef<
  HTMLElement,
  ContentEditableProps
>(
  (
    {
      className,
      editButtonLabel,
      onContentChange,
      children,
      editButton = true,
      ...props
    },
    ref,
  ) => {
    const currentContent = useRef<string | null>(null);
    const [editing, setEditing] = useState(false);

    const containerRef = useRef<HTMLElement>(null);

    useImperativeHandle(ref, () => containerRef.current as HTMLElement);

    return (
      <div className="group w-full">
        <Slot
          {...props}
          className={contentEditableVariant({ className, editButton })}
          ref={containerRef}
          // Without the pencil there is no element carrying the accessible
          // name, so the editable region itself takes the role and label.
          role={editButton ? undefined : 'textbox'}
          aria-label={editButton ? undefined : editButtonLabel}
          contentEditable
          onFocus={(e) => {
            currentContent.current = e.currentTarget.textContent;
            setEditing(true);
            // Only reposition the caret for non-pointer focus (the pencil's
            // programmatic .focus() or keyboard Tab). A mouse click already
            // placed the caret at the click point — clobbering it would yank
            // the caret to the end on every click-to-edit.
            if (
              e.currentTarget.matches(':focus-visible') &&
              containerRef.current
            ) {
              window.getSelection()?.selectAllChildren(containerRef.current);
              window.getSelection()?.collapseToEnd();
            }
          }}
          onBlur={(e) => {
            setEditing(false);
            const newContent = e.currentTarget.textContent;

            if (newContent === currentContent.current) {
              return;
            }

            // Whitespace-only counts as empty: it would read as blank and,
            // trimmed by a consumer, could persist as '' and wipe the value.
            if (!newContent?.trim()) {
              e.currentTarget.textContent = currentContent.current ?? '';
              return;
            }

            onContentChange?.(newContent);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          suppressContentEditableWarning
        >
          {children}
        </Slot>
        {editButton && !editing && (
          <SimpleTooltip label={editButtonLabel ?? 'Edit'}>
            <div className="-my-1 ml-1 inline-block h-12">
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  containerRef.current?.focus();
                }}
                aria-label={editButtonLabel ?? 'Edit'}
              >
                <Icon name="Pencil" size="auto" />
              </Button>
            </div>
          </SimpleTooltip>
        )}
      </div>
    );
  },
);
